import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import WorkshopCard from './WorkshopCard';

function ClientApp() {
  const [workshops, setWorkshops] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch workshops from the correct collection
        console.log('Fetching workshops...');
        const workshopsQuery = query(
          collection(db, 'sessions_workshops'),
          where('status', '==', 'published')
        );
        const workshopsSnapshot = await getDocs(workshopsQuery);
        let workshopsData = workshopsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Published workshops found:', workshopsData.length);
        
        // If no published workshops, fetch all workshops for debugging
        if (workshopsData.length === 0) {
          console.log('No published workshops found, fetching all workshops...');
          const allWorkshopsSnapshot = await getDocs(collection(db, 'sessions_workshops'));
          const allWorkshopsData = allWorkshopsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('All workshops:', allWorkshopsData);
          console.log('Workshop statuses:', allWorkshopsData.map(w => ({ id: w.id, status: w.status, title: w.title })));
          
          // Show draft workshops too for now
          workshopsData = allWorkshopsData;
        }

        // Fetch instructors
        const usersQuery = query(
          collection(db, 'users'),
          where('userType', '==', 'instructor')
        );
        const instructorsSnapshot = await getDocs(usersQuery);
        const instructorsData = instructorsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log('Instructors found:', instructorsData.length);
        console.log('Final workshops data:', workshopsData);

        setWorkshops(workshopsData);
        setInstructors(instructorsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredWorkshops = workshops.filter(workshop => {
    const locationMatch = selectedLocation === 'all' || workshop.location === selectedLocation;
    const typeMatch = selectedType === 'all' || workshop.category === selectedType;
    return locationMatch && typeMatch;
  });

  const uniqueLocations = [...new Set(workshops.map(w => w.location).filter(Boolean))];
  const uniqueTypes = [...new Set(workshops.map(w => w.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-panel p-8 text-center">
          <div className="w-8 h-8 bg-klorah-primary rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-klorah-text">Loading workshops...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 px-6 sm:px-8 lg:px-12 bg-gradient-to-b from-klorah-dark to-klorah-card/30 border-b border-klorah-border">
        <div className="max-w-7xl mx-auto text-center">
          <div className="space-y-6">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
              Photography
              <span className="block bg-gradient-to-r from-klorah-primary to-klorah-secondary bg-clip-text text-transparent">Workshops</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-klorah-text max-w-3xl mx-auto">
              Learn from world-class photographers in stunning locations around the globe
            </p>

            {/* Stats Row */}
            <div className="flex justify-center items-center space-x-12 pt-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-klorah-primary">{workshops.length}</div>
                <div className="text-sm text-klorah-text-light uppercase tracking-wider">Workshops</div>
              </div>
              <div className="w-px h-8 bg-klorah-border"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-klorah-primary">{instructors.length}</div>
                <div className="text-sm text-klorah-text-light uppercase tracking-wider">Instructors</div>
              </div>
              <div className="w-px h-8 bg-klorah-border"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-klorah-primary">{uniqueLocations.length}</div>
                <div className="text-sm text-klorah-text-light uppercase tracking-wider">Locations</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Instructors */}
      {instructors.length > 0 && (
        <section className="py-16 px-6 sm:px-8 lg:px-12 bg-klorah-card/30 border-b border-klorah-border">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Meet Our Instructors
              </h2>
              <p className="text-lg text-klorah-text max-w-2xl mx-auto">
                Learn from award-winning photographers who have mastered their craft
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {instructors.slice(0, 3).map((instructor, index) => (
                <div 
                  key={instructor.id} 
                  className="glass-panel p-6"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-klorah-primary to-klorah-secondary border border-klorah-border mx-auto mb-4 rounded-full flex items-center justify-center">
                    {instructor.profileImage ? (
                      <img 
                        src={instructor.profileImage} 
                        alt={instructor.displayName}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <span className="text-xl font-medium text-black">
                          {instructor.displayName?.[0]?.toUpperCase()}
                        </span>
                    )}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-2 text-center">
                    {instructor.displayName || 'Anonymous Instructor'}
                  </h3>
                  
                  <p className="text-klorah-text mb-4 text-center">
                    {instructor.bio || 'Photography instructor and workshop leader.'}
                  </p>
                  
                  <div className="pt-4 border-t border-klorah-border">
                    <p className="text-sm text-klorah-text-light text-center">
                      {instructor.location || 'Location not specified'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="py-12 px-6 sm:px-8 lg:px-12 bg-klorah-dark border-b border-klorah-border">
        <div className="max-w-7xl mx-auto">
          <div className="glass-panel p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="location" className="block text-sm font-medium text-white">
                  Filter by Location
                </label>
                <select
                  id="location"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full px-3 py-2 bg-klorah-card border border-klorah-border text-white rounded-xl focus:border-klorah-primary focus:outline-none"
                >
                  <option value="all">All Locations</option>
                  {uniqueLocations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="type" className="block text-sm font-medium text-white">
                  Filter by Type
                </label>
                <select
                  id="type"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full px-3 py-2 bg-klorah-card border border-klorah-border text-white rounded-xl focus:border-klorah-primary focus:outline-none"
                >
                  <option value="all">All Types</option>
                  {uniqueTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workshops Grid */}
      <section className="py-12 px-6 sm:px-8 lg:px-12 bg-klorah-card/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Available Workshops
            </h2>
            <p className="text-lg text-klorah-text">
              {filteredWorkshops.length} workshop{filteredWorkshops.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {filteredWorkshops.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredWorkshops.map((workshop) => (
                <WorkshopCard key={workshop.id} workshop={workshop} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="glass-panel p-8">
                <p className="text-klorah-text text-lg">No workshops found matching your criteria.</p>
                <button
                  onClick={() => {
                    setSelectedLocation('all');
                    setSelectedType('all');
                  }}
                  className="mt-4 klorah-button"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default ClientApp; 